import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import { getAdminUserColumns } from "./Columns";
import "./RiskTable.css";
import { addUserDepartment, addUserCompany } from "../../../store/actions/adminActions"
import SortDown from "../../../assets/images/sortdown.svg";
import SortUp from "../../../assets/images/sortup.svg";
import { connect } from "react-redux";
import { compose } from "redux";

function Avatar({ name, src, size = 28 }) {
    const [errored, setErrored] = React.useState(false);

    const initials = (name || "")
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

    if (!src || errored) {
        return (
            <div
                className="flex items-center justify-center rounded-full border border-gray-300 bg-gray-100 text-gray-500"
                style={{ width: size, height: size }}
                title={name}
            >
                <svg
                    width={Math.floor(size * 0.6)}
                    height={Math.floor(size * 0.6)}
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                >
                    <path d="M10 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 1c-3.33 0-6 1.34-6 3v2h12v-2c0-1.66-2.67-3-6-3Z" />
                </svg>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={name}
            title={name}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="rounded-full object-cover border border-gray-300 bg-white"
            style={{ width: size, height: size }}
            onError={() => setErrored(true)}
        />
    );
}

const RiskCenterUserGridCard = ({ user, t, formatUserStatusBadge }) => {
    const [errored, setErrored] = React.useState(false);
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "-";
    const isActive = String(user.status || "").toLowerCase() === "active";
    const role = user.role
        ? t(`adminManagement.users.roles.${String(user.role).toLowerCase()}`, { defaultValue: String(user.role) })
        : null;
    const joinDate = (() => {
        const raw = user.join_date || user.created_at || user.createdAt || "";
        if (!raw) return "-";
        const d = new Date(raw);
        if (isNaN(d.getTime())) return String(raw);
        return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
    })();
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col items-center">
            <div className="w-16 h-16 rounded-full overflow-hidden border border-gray-200 flex-shrink-0 mb-3">
                {user.avatar && !errored ? (
                    <img src={user.avatar} alt={fullName} referrerPolicy="no-referrer" onError={() => setErrored(true)} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="8" r="3.2" strokeWidth="1.6" />
                            <path d="M4 19c1.4-3.1 4.3-4.8 8-4.8s6.6 1.7 8 4.8" strokeWidth="1.6" />
                        </svg>
                    </div>
                )}
            </div>
            <p className="text-[15px] font-semibold text-gray-900 text-center leading-tight">{fullName}</p>
            {role && (
                <span className="mt-2 inline-flex items-center px-3 py-0.5 rounded-full text-[12px] font-semibold bg-[#0B0B0F] text-white">
                    {role}
                </span>
            )}
            <div className="w-full border-t border-gray-100 my-4" />
            <div className="w-full space-y-2.5 text-[13px] text-gray-600">
                <div className="flex items-center gap-2.5">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M2 7l10 7 10-7" />
                    </svg>
                    <span className="truncate">{user.email || "-"}</span>
                </div>
                <div className="flex items-center gap-2.5">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="1" /><path d="M9 7h6M9 11h6M9 15h4" />
                    </svg>
                    <span>{user.department_name || t("adminManagement.users.notApplicable")}</span>
                </div>
                <div className="flex items-center gap-2.5">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? "bg-green-500" : "bg-gray-400"}`} />
                    <span>{formatUserStatusBadge(user.status)}</span>
                </div>
                <div className="flex items-center gap-2.5">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="3.2" /><path d="M4 19c1.4-3.1 4.3-4.8 8-4.8s6.6 1.7 8 4.8" />
                    </svg>
                    <span>{joinDate}</span>
                </div>
            </div>
        </div>
    );
};

const RiskCenterTable = ({ data, openpopup, companyId, addUserCompanyFN }) => {
    const { t } = useTranslation();

    const [q, setQ] = useState("");
    const [dept, setDept] = useState("All");
    const [status, setStatus] = useState("All");
    const [role, setRole] = useState("All");
    const [viewMode, setViewMode] = useState("table");
    const [memberCompanyId, setMemberCompanyId] = useState(null);
    const [memberSearch, setMemberSearch] = useState("");
    const [memberSuccess, setMemberSuccess] = useState("");



    const norm = (v) =>
        String(v ?? "")
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();

    const departmentOptions = useMemo(() => {
        const seen = new Map();
        (data || []).forEach((u) => {
            const raw = u.department ?? u.department_name ?? u.dept ?? "";
            const key = norm(raw);
            if (key && !seen.has(key)) seen.set(key, String(raw));
        });
        return ["All", ...Array.from(seen.values())];
    }, [data]);

    const statusOptions = useMemo(() => {
        const seen = new Set(["All"]);
        (data || []).forEach((u) => {
            const raw = String(u.status || "").trim();
            if (raw) seen.add(raw);
        });
        return Array.from(seen);
    }, [data]);

    const roleOptions = useMemo(() => {
        const seen = new Set(["All"]);
        (data || []).forEach((u) => {
            const raw = String(u.role || "").trim();
            if (raw) seen.add(raw);
        });
        return Array.from(seen);
    }, [data]);

    const filteredData = useMemo(() => {
        const tokens = norm(q).split(" ").filter(Boolean);
        const statusKey = norm(status);
        const roleKey = norm(role);

        return (data || []).filter((u) => {
            const isInCompany = u.companies?.some(
                (c) => Number(c.company_id) === Number(companyId)
            );

            if (!isInCompany) return false;

            const first = u.first_name ?? u.firstName ?? "";
            const last = u.last_name ?? u.lastName ?? "";
            const full = [first, last].filter(Boolean).join(" ");
            const name = u.name ?? full;

            const haystack = [
                name,
                full,
                first,
                last,
                u.email,
                u.username,
                u.role,
                u.title,
                u.id,
                u.user_id,
            ]
                .map(norm)
                .filter(Boolean)
                .join(" ");

            const matchQ =
                tokens.length === 0 || tokens.every((t) => haystack.includes(t));

            const rowStatus = norm(u.status ?? "");
            const rowRole = norm(u.role ?? "");

            const matchStatus = status === "All" || rowStatus === statusKey;
            const matchRole = role === "All" || rowRole === roleKey;

            return matchQ && matchStatus && matchRole;
        });
    }, [data, q, role, status, companyId]);

    const defaultColumn = useMemo(
        () => ({
            Filter: () => null,
        }),
        []
    );

    const columns = useMemo(() => getAdminUserColumns(t), [t]);

    const formatStatusOption = (opt) =>
        opt === "All"
            ? t("adminManagement.users.allStatuses")
            : t(`adminManagement.users.statuses.${String(opt).toLowerCase()}`, { defaultValue: opt });
    const formatRoleOption = (opt) =>
        opt === "All"
            ? t("adminManagement.users.allRoles")
            : t(`adminManagement.users.roles.${String(opt).toLowerCase()}`, { defaultValue: opt });

    const formatUserStatusBadge = (raw) => {
        const isActive = String(raw || "").toLowerCase() === "active";
        return isActive
            ? t("adminManagement.users.statuses.active")
            : t("adminManagement.users.statuses.inactive");
    };

    const filteredMemberUsers = useMemo(() => {
        const q = memberSearch.toLowerCase().trim();

        return (data || []).filter((user) => {
            const name = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
            const email = (user.email || "").toLowerCase();

            return name.includes(q) || email.includes(q);
        });
    }, [data, memberSearch]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state: { pageIndex },
        pageOptions,
    } = useTable(
        {
            columns,
            data: filteredData,
            defaultColumn,
            initialState: { pageIndex: 0, pageSize: 10 },
            autoResetPage: false,
        },
        useFilters,
        useSortBy,
        usePagination
    );

    return (
        <div className="incidents">
            <div className="ml-6 mr-6">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="min-w-0">
                            <h2 className="text-xl sm:text-lg font-semibold text-gray-900 leading-tight">
                                {t("adminManagement.users.title")}
                            </h2>
                            <p className="text-sm text-gray-500 leading-snug">{t("adminManagement.users.subtitle")}</p>
                        </div>
                        <button
                            onClick={() => {
                                setMemberCompanyId(companyId);
                                setMemberSearch("");
                            }}
                            className="self-start inline-flex items-center gap-1.5 h-9 sm:h-10 px-3 sm:px-4 rounded-lg bg-[#0B0B0F] text-white hover:opacity-90 transition text-sm sm:text-sm font-medium shrink-0"
                        >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeWidth="2" d="M12 5v14M5 12h14" />
                            </svg>
                            <span>{t("adminManagement.users.addUser")}</span>
                        </button>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_160px_160px_auto] gap-3">
                        <div className="relative">
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t("adminManagement.users.searchPlaceholder")}
                                className="w-full h-10 pl-9 pr-3 rounded-lg border border-gray-200 bg-gray-100/60 focus:bg-white focus:border-gray-300 focus:outline-none"
                            />
                            <svg
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                            >
                                <circle cx="11" cy="11" r="7" strokeWidth="1.8" />
                                <path d="M20 20l-3.5-3.5" strokeWidth="1.8" />
                            </svg>
                        </div>

                        {/* <select
                            value={dept}
                            onChange={(e) => setDept(e.target.value)}
                            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none"
                        >
                            {departmentOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt === "All" ? "All Risk Centers" : opt}
                                </option>
                            ))}
                        </select> */}

                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none"
                        >
                            {statusOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                    {formatStatusOption(opt)}
                                </option>
                            ))}
                        </select>

                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none"
                        >
                            {roleOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                    {formatRoleOption(opt)}
                                </option>
                            ))}
                        </select>

                        <div className="hidden md:flex items-center gap-1 bg-[#F3F4F6] p-1 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setViewMode("grid")}
                                className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-[13px] font-medium transition ${viewMode === "grid"
                                        ? "bg-white text-[#111827] shadow-sm"
                                        : "text-[#6B7280] hover:text-[#111827]"
                                    }`}
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <rect x="3" y="3" width="7" height="7" strokeWidth="1.8" />
                                    <rect x="14" y="3" width="7" height="7" strokeWidth="1.8" />
                                    <rect x="14" y="14" width="7" height="7" strokeWidth="1.8" />
                                    <rect x="3" y="14" width="7" height="7" strokeWidth="1.8" />
                                </svg>
                                {t("adminManagement.view.grid")}
                            </button>

                            <button
                                type="button"
                                onClick={() => setViewMode("table")}
                                className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-[13px] font-medium transition ${viewMode === "table"
                                        ? "bg-[#0B0B0F] text-white shadow-sm"
                                        : "text-[#6B7280] hover:text-[#111827]"
                                    }`}
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <line x1="3" y1="6" x2="21" y2="6" strokeWidth="1.8" />
                                    <line x1="3" y1="12" x2="21" y2="12" strokeWidth="1.8" />
                                    <line x1="3" y1="18" x2="21" y2="18" strokeWidth="1.8" />
                                </svg>
                                {t("adminManagement.view.table")}
                            </button>
                        </div>
                    </div>

                    {viewMode === "grid" && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {filteredData.map((user) => (
                                <RiskCenterUserGridCard key={user.user_id} user={user} t={t} formatUserStatusBadge={formatUserStatusBadge} />
                            ))}
                        </div>
                    )}

                    {viewMode === "table" && (
                        <div className="hidden md:block mt-4 rounded-2xl border border-gray-200 overflow-x-auto overflow-y-visible">
                            <table {...getTableProps()} className="w-full text-sm text-gray-800">
                                <thead className="bg-white">
                                    {headerGroups.map((hg) => {
                                        const { key: hgKey, ...hgRowProps } = hg.getHeaderGroupProps();
                                        return (
                                            <tr key={hgKey} {...hgRowProps} className="border-b border-gray-200">
                                                {hg.headers.map((col) => {
                                                    if (col.dontshow) return null;
                                                    const { key: thKey, ...thProps } = col.getHeaderProps(
                                                        col.getSortByToggleProps()
                                                    );
                                                    return (
                                                        <th
                                                            key={thKey}
                                                            {...thProps}
                                                            className="text-left font-medium text-gray-700"
                                                            style={{ padding: "12px 16px" }}
                                                        >
                                                            <div className="flex items-center gap-1.5">
                                                                {col.render("Header")}

                                                                <span className="sort-icon opacity-60 inline-flex items-center">
                                                                    {col.isSorted
                                                                        ? col.isSortedDesc
                                                                            ? <img src={SortDown} alt="▼" className="w-3 h-3 object-contain" />
                                                                            : <img src={SortUp} alt="▲" className="w-3 h-3 object-contain" />
                                                                        : null}
                                                                </span>
                                                            </div>
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </thead>

                                <tbody {...getTableBodyProps()} className="bg-white">
                                    {page.map((row) => {
                                        prepareRow(row);
                                        const { key: rowKey, ...rowProps } = row.getRowProps();
                                        return (
                                            <tr
                                                key={rowKey}
                                                {...rowProps}
                                                className="hover:bg-gray-50 transition-colors border-t border-gray-100"
                                            >
                                                {row.cells.map((cell) => {
                                                    if (cell.column.dontshow) return null;
                                                    const cellId = String(cell.column.id || "").toLowerCase();
                                                    const { key: cellKey, ...cellProps } = cell.getCellProps();

                                                    return (
                                                        <td
                                                            key={cellKey}
                                                            {...cellProps}
                                                            className={
                                                                cellId === "actions"
                                                                    ? "cursor-pointer relative z-20 overflow-visible"
                                                                    : "cursor-pointer"
                                                            }
                                                            style={{ padding: "12px 16px" }}
                                                        >
                                                            {cell.render("Cell")}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="text-sm text-gray-500 mt-3 flex items-center justify-between">
                        <span>
                            {t("adminManagement.users.showingUsers", {
                                shown: page?.length ?? 0,
                                total: filteredData?.length ?? 0,
                            })}
                        </span>
                        <span>
                            {t("adminManagement.users.pageOf", {
                                current: pageIndex + 1,
                                pages: Math.max(pageOptions?.length || 1, 1),
                            })}
                        </span>
                    </div>

                    {memberCompanyId && (
                        <div
                            className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/30"
                            onClick={() => {
                                setMemberCompanyId(null);
                                setMemberSearch("");
                            }}
                        >
                            <div
                                className="w-full max-w-[420px] rounded-[16px] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-[#E5E7EB] p-4"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="mb-3">
                                    <div className="text-[16px] font-semibold text-[#111827]">
                                        {t("riskCenterPortal.addMember.title")}
                                    </div>
                                    <div className="text-[13px] text-[#6B7280] mt-1">
                                        {t("riskCenterPortal.addMember.subtitleRiskCenter")}
                                    </div>
                                </div>

                                <input
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                    placeholder={t("riskCenterPortal.addMember.searchPlaceholder")}
                                    className="w-full h-[40px] rounded-[10px] border border-[#E5E7EB] px-3 text-[13px] outline-none focus:border-[#D1D5DB]"
                                />

                                <div className="mt-3 max-h-[280px] overflow-y-auto space-y-1">
                                    {filteredMemberUsers.map((user) => {
                                        const alreadyInCompany = user.companies?.some(
                                            (c) => Number(c.company_id) === Number(memberCompanyId)
                                        );

                                        return (
                                            <button
                                                key={user.user_id}
                                                disabled={alreadyInCompany}
                                                onClick={async () => {
                                                    if (alreadyInCompany) return;

                                                    try {
                                                        await addUserCompanyFN(memberCompanyId, user.user_id);

                                                        setMemberSuccess(t("riskCenterPortal.addMember.userAddedSuccess"));
                                                        setMemberCompanyId(null);
                                                        setMemberSearch("");

                                                        setTimeout(() => {
                                                            setMemberSuccess("");
                                                        }, 3000);
                                                    } catch (err) {
                                                        console.error("Failed to add member:", err);
                                                    }
                                                }}
                                                className={`w-full flex items-center gap-3 rounded-[10px] px-3 py-2 text-left transition ${alreadyInCompany
                                                    ? "bg-[#F3F4F6] cursor-not-allowed opacity-60"
                                                    : "hover:bg-[#F9FAFB]"
                                                    }`}
                                            >
                                                <Avatar
                                                    name={`${user.first_name || ""} ${user.last_name || ""}`.trim()}
                                                    src={user.avatar}
                                                    size={32}
                                                />

                                                <div className="min-w-0 flex-1">
                                                    <div
                                                        className={`truncate text-[13px] font-medium ${alreadyInCompany ? "text-[#9CA3AF]" : "text-[#111827]"
                                                            }`}
                                                    >
                                                        {`${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                                                            "Unnamed User"}
                                                    </div>

                                                    <div
                                                        className={`truncate text-[12px] ${alreadyInCompany ? "text-[#9CA3AF]" : "text-[#6B7280]"
                                                            }`}
                                                    >
                                                        {user.email}
                                                    </div>
                                                </div>

                                                {alreadyInCompany && (
                                                    <div className="text-[11px] text-[#9CA3AF] whitespace-nowrap">
                                                        {t("riskCenterPortal.addMember.alreadyAdded")}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}

                                    {!filteredMemberUsers.length && (
                                        <div className="px-3 py-6 text-center text-[13px] text-[#9CA3AF]">
                                            {t("riskCenterPortal.addMember.noUsersFound")}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 flex justify-end">
                                    <button
                                        onClick={() => {
                                            setMemberCompanyId(null);
                                            setMemberSearch("");
                                        }}
                                        className="h-[36px] rounded-[10px] border border-[#E5E7EB] px-4 text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
                                    >
                                        {t("riskCenterPortal.addMember.close")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

const mapStateToProps = (state) => {

    return {

    };
};

const mapDispatchToProps = (dispatch) => {

    return {

        addUserCompanyFN: (company, user) => dispatch(addUserCompany(company, user))
    };
};


export default compose(connect(mapStateToProps, mapDispatchToProps))(RiskCenterTable);


